import React, { useEffect, useMemo, useRef, useState } from 'react';
import HorizontalDotsButton from '@/components/HorizontalDotsButton/HorizontalDotsButton';
import CircleNumber from '@/components/CircleNumber/CircleNumber';
import Dropdown, { ListItem } from '@/components/Dropdown/Dropdown';
import {
	ChatGPT,
	Checkmark,
	CloseThin,
	DropDown
} from '@/resources/assets/images';
import { EditIcon } from '@/resources/assets/images/components-icons';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import RichEditor, { DraftEditor } from '@/components/RichEditor/RichEditor';
import EmailSmartTemplates from '@/modules/email/components/EmailSmartTemplates/EmailSmartTemplates';
import { editSequenceSteps, updateSequence } from '@/services/sequencesService';
import { sendAnalytics } from '@/services/analyticsService';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import { toast } from 'react-hot-toast';
import { useSteps } from '../hooks/useSteps';
import { useEmailTemplates } from '../hooks/useEmailTemplates';
import type { StepsType } from '../hooks/useSequences';
import { useSequencesContext } from '../SequencesContext';
import NewSequenceFooter from '../components/NewSequenceFooter';
import '../sequences.scss';

export default function SequenceStepCreationPage() {
	const {
		selectedStep,
		selectedTemplate,
		setSelectedTemplate,
		selectedSequence,
		setSelectedSequence,
		sequencePage
	} = useSequencesContext();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const subjectRef = useRef<HTMLInputElement>(null);
	const bodyRef = useRef<DraftEditor>(null);
	const [subject, setSubject] = useState(selectedStep?.subject || '');
	const [emailBody, setEmailBody] = useState(selectedStep?.body || '');
	const [shouldSaveEmail, setShouldSaveEmail] = useState(false);
	const isEditingRef = useRef(false);
	const [isEditing, setIsEditing] = useState(false);
	const [contextMenuValue, setContextMenuValue] = useState<string>();
	const [refreshContextMenu, setRefreshContextMenu] = useState(false);
	const editingLast = useRef<'SUBJECT' | 'EMAIL'>();

	useEffect(() => {
		setEmailBody(selectedStep?.body || '');
		setSubject(selectedStep?.subject || '');
	}, [selectedStep, sequencePage === 'STEPS-CREATION']);

	function handleSaveEmail() {
		if (!selectedTemplate || !selectedStep || !selectedSequence) {
			return;
		}

		const newStep = {
			...selectedStep,
			subject: subjectRef.current?.value || '',
			body: emailBody
		};

		const changedSubject = newStep.subject !== selectedStep.subject;
		const changedBody = newStep.body !== selectedStep.body;

		const newSteps = selectedTemplate.steps.map(step => {
			if (step.id === selectedStep.id) {
				return newStep;
			}
			return step;
		});

		setSelectedTemplate({
			...selectedTemplate,
			steps: newSteps
		});

		setSelectedSequence({
			...selectedSequence,
			steps: newSteps
		});

		if (changedSubject) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				sequenceId: selectedSequence?.id,
				stepNumber: selectedStep?.order,
				eventName: 'user_edited_subject_line_sequences'
			});
		}
		if (changedBody) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				sequenceId: selectedSequence?.id,
				stepNumber: selectedStep?.order,
				eventName: 'user_edited_email_body_sequences'
			});
		}
	}

	function handleChangeTimeToSend(e: ListItem) {
		if (!selectedTemplate || !selectedStep || !selectedSequence) return;
		const newStep = {
			...selectedStep,
			sendAfterInterval: Number(e.id)
		};
		const newSteps = selectedTemplate?.steps.map(step => {
			if (step.order === newStep.order) {
				return newStep;
			}
			return step;
		});
		setSelectedTemplate({
			...selectedTemplate,
			steps: newSteps
		});
		setSelectedSequence({
			...selectedSequence,
			steps: newSteps
		});

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: selectedSequence.id,
			sendAfter: `${newStep.sendAfterInterval} ${newStep.sendAfterType}`,
			eventName: 'user_changed_sequence_step_duration'
		});
	}

	useEffect(() => {
		if (shouldSaveEmail) {
			handleSaveEmail();
			setShouldSaveEmail(false);
		}
	}, [shouldSaveEmail]);

	function handleEditBlur() {
		isEditingRef.current = false;
		setIsEditing(false);
		subjectRef.current?.blur();
		setShouldSaveEmail(true);
	}

	function handleEditFocus() {
		setEmailBody(prev => `${prev} `);
		isEditingRef.current = true;
		setIsEditing(true);
	}

	useEffect(() => {
		function handleMouseDown(e: MouseEvent) {
			const element = e.target as HTMLElement;
			const elementId = element.id;

			const allowedElementIdsToBlur = [
				'email-editor',
				'subject-editor',
				'custom-text',
				'dropdown-menu-item'
			];

			if (isEditingRef.current) {
				if (!allowedElementIdsToBlur.includes(elementId)) {
					handleEditBlur();
				}
			}
			if (!isEditingRef.current) {
				if (
					elementId === 'email-editor' ||
					elementId === 'subject-editor'
				) {
					handleEditFocus();
				}
			}
			if (elementId === 'subject-editor') {
				editingLast.current = 'SUBJECT';
			}

			if (elementId === 'email-editor') {
				editingLast.current = 'EMAIL';
			}
		}
		document.addEventListener('mousedown', handleMouseDown);
		return () => {
			document.removeEventListener('mousedown', handleMouseDown);
		};
	}, []);

	function handleAddCustomText(customText: string) {
		isEditingRef.current = true;
		setIsEditing(true);

		if (customText === '[ai_snippet]' || editingLast.current === 'EMAIL') {
			setContextMenuValue(customText);
			setRefreshContextMenu(prev => !prev);
			return;
		}
		if (editingLast.current === 'SUBJECT') {
			if (!subjectRef.current) return;

			// Get cursor position
			const start = Number(subjectRef.current.selectionStart);
			const end = Number(subjectRef.current.selectionEnd);
			const text = subjectRef.current.value;
			subjectRef.current.value =
				text.slice(0, start) + customText + text.slice(end);

			setSubject(subjectRef.current.value);
		}
	}

	function aiSnippetButtonHandler() {
		if (emailBody.includes('[ai_snippet]')) {
			setEmailBody(emailBody.replace('[ai_snippet]', ''));
			return;
		}

		handleAddCustomText('[ai_snippet]');
	}

	const checkEmailBodyLengthEqualsZero =
		emailBody
			.replace(/(<([^>]+)>)/gi, '')
			.replace(/\s+/g, '')
			.replace(/&nbsp;/g, '').length === 0;

	return (
		<div className="sequences-component-holder flex h-full w-full pb-16">
			<div className="flex h-full w-2/5 flex-col gap-2 px-3">
				<h2 className="whitespace-nowrap text-2xl font-medium text-gray-200">
					Step creation
				</h2>
				<p>
					Design a sequence of up to seven steps, consisting of
					initial and follow-up emails. Enables you to outline and
					organize your email sequence in stages, from the first
					contact to subsequent follow-ups
				</p>
				<StepCreationList />
			</div>
			<div className="flex w-3/5 flex-col">
				<div className="flex h-10 w-full items-end">
					<span className="mr-4 whitespace-nowrap">
						Automatic Email
					</span>
					<div className="z-150 h-full text-center text-lg text-orange">
						{(selectedStep?.sendAfterInterval || 0) > 0 && (
							<Dropdown
								key={selectedStep?.id}
								title={`Send after ${selectedStep?.sendAfterInterval} days`}
								headerColor="medium"
								dropdownColor="medium"
								width="100%"
								handleClick={handleChangeTimeToSend}
								list={Array.from({ length: 15 }, (_, i) => ({
									id: i + 1,
									title: `Send after ${i + 1} day${
										i > 0 ? 's' : ''
									}`
								}))}
								listClassName="sequence-dropdown-days"
							/>
						)}
					</div>
				</div>
				<span className="text-gray-200">Click to edit</span>
				<div className="my-3 flex items-center justify-between gap-3">
					<EmailSubjectEdit
						ref={subjectRef}
						subject={subject}
						isEditing={isEditing}
					/>
					<EmailTemplatesDropdown />
					<AddCustomText handleAddCustomText={handleAddCustomText} />
				</div>
				<div className="email-body-components flex h-full w-full overflow-y-scroll rounded-lg border border-dark-grey">
					<div className="email-body-holder w-full overflow-auto">
						<EmailBodyEdit
							ref={bodyRef}
							emailBody={emailBody}
							setEmailBody={setEmailBody}
							isEditing={isEditing}
							contextMenuValue={contextMenuValue}
							refreshContextMenu={refreshContextMenu}
						/>
					</div>
					{/* <div className="w-1/3 my-1 overflow-y-auto smart-template-holder"> */}
					<div className="smart-template-holder my-1 w-[240px] max-w-[240px] overflow-x-visible">
						<SmartTemplates />
					</div>
				</div>
				{selectedStep?.order === 0 && (
					<div className="mt-3">
						<button
							type="button"
							className={`ai-button snippet ${checkEmailBodyLengthEqualsZero ? 'disabled' : ''} ${emailBody.includes('[ai_snippet]') ? 'added' : ''} `}
							disabled={checkEmailBodyLengthEqualsZero}
							onClick={aiSnippetButtonHandler}
						>
							{emailBody.includes('[ai_snippet]')
								? 'Remove AI Snippet'
								: 'Add AI Snippet'}
							<ChatGPT />
						</button>
					</div>
				)}
			</div>
			<NewSequenceFooter />
		</div>
	);
}

function AddCustomText({
	handleAddCustomText
}: {
	handleAddCustomText: (customText: string) => void;
}) {
	const customTextOptions = [
		{
			id: '1',
			label: '[business_name]',
			onClick: () => handleAddCustomText('[business_name]')
		}
	];

	return (
		<div className="h-full w-min rounded-md border border-dark-grey text-gray-200">
			<DropdownMenu
				options={customTextOptions || []}
				position="bottom"
				itemClassName="bg-dm-black3 border-b border-dark-grey py-3 px-2 hover:bg-orange hover:text-dm-black"
			>
				<div
					id="custom-text"
					className="flex h-full w-full items-center justify-between gap-3 px-4 text-gray-200"
				>
					<span id="custom-text" className="whitespace-nowrap">
						Add custom text
					</span>
					<DropDown id="custom-text" />
				</div>
			</DropdownMenu>
		</div>
	);
}

function EmailTemplatesDropdown() {
	const { emailTemplates } = useEmailTemplates();
	const {
		selectedStep,
		setSelectedStep,
		selectedTemplate,
		setSelectedTemplate,
		selectedSequence,
		setSelectedSequence
	} = useSequencesContext();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const availableTemplates = useMemo(
		() =>
			emailTemplates?.filter(
				template =>
					selectedStep?.order === 0 ||
					!template.body?.includes('[ai_snippet]')
			),
		[selectedStep, emailTemplates]
	);

	const options = availableTemplates?.map(template => ({
		id: template.id,
		label: template.name,
		onClick: () => {
			if (!selectedStep || !selectedTemplate || !selectedSequence) return;
			setSelectedStep({
				...selectedStep,
				subject: template.subject,
				body: template.body
			});
			const newSteps = selectedTemplate?.steps.map(step => {
				if (step.order === selectedStep.order) {
					return {
						...step,
						subject: template.subject,
						body: template.body
					};
				}
				return step;
			});
			setSelectedTemplate({
				...selectedTemplate,
				steps: newSteps
			});
			setSelectedSequence({
				...selectedSequence,
				steps: newSteps
			});

			sendAnalytics({
				type: 'track',
				userId: user.email,
				sequenceId: selectedSequence.id,
				emailTemplateId: template.id,
				eventName: 'user_selected_email_template_sequences'
			});
		}
	}));

	return (
		<div className="h-full w-3/4 rounded-md border border-dark-grey text-gray-200">
			<DropdownMenu
				options={options || []}
				position="bottom"
				itemClassName="bg-dm-black3 border-b border-dark-grey py-3 px-2 hover:bg-dm-gold hover:text-dm-black"
			>
				<div className="flex h-full w-full items-center justify-between whitespace-nowrap px-4 text-gray-200">
					<span>Adopt template</span>
					<DropDown />
				</div>
			</DropdownMenu>
		</div>
	);
}

const EmailSubjectEdit = React.forwardRef<HTMLInputElement, any>(
	({ subject, isEditing }, ref) => {
		const [subjectEdit, setSubjectEdit] = useState(subject);

		function handleSubjectChange(e: React.ChangeEvent<HTMLInputElement>) {
			setSubjectEdit(e.target.value);
		}

		useEffect(() => {
			setSubjectEdit(subject);
		}, [subject]);

		return (
			<div className="w-full">
				<input
					id="subject-editor"
					ref={ref}
					type="text"
					className={`w-full border-dark-grey ${
						isEditing ? 'bg-gray-100 text-black' : 'bg-dm-black'
					}`}
					value={subjectEdit}
					onChange={handleSubjectChange}
				/>
			</div>
		);
	}
);

interface EmailBodyEditProps {
	emailBody: string;
	setEmailBody: (emailBody: string) => void;
	isEditing: boolean;
	contextMenuValue?: string;
	refreshContextMenu?: boolean;
}
const EmailBodyEdit = React.forwardRef<DraftEditor, EmailBodyEditProps>(
	(
		{
			emailBody,
			setEmailBody,
			isEditing,
			contextMenuValue,
			refreshContextMenu
		},
		ref
	) => {
		function handleEmailBodyChange(e: string) {
			setEmailBody(e);
		}

		return (
			<div className="h-full">
				<RichEditor
					ref={ref}
					onChange={handleEmailBodyChange}
					value={emailBody}
					className={`h-full w-full ${
						!isEditing
							? 'bg-dm-black text-gray-300'
							: 'bg-gray-100 text-black'
					}`}
					disabled={!isEditing}
					disableDisabledStyles
					enablePropagation
					contextMenuValue={contextMenuValue}
					refreshContextMenu={refreshContextMenu}
				/>
			</div>
		);
	}
);

function SmartTemplates() {
	const { selectedStep } = useSequencesContext();

	if (!selectedStep?.id) return null;

	return (
		<div className="flex h-full w-full flex-col border-l border-dark-grey text-gray-200">
			<EmailSmartTemplates selectedTemplate={selectedStep} />
		</div>
	);
}

function StepCreationList() {
	const scrollRef = useRef<HTMLDivElement>(null);

	const {
		selectedTemplate,
		setSelectedTemplate,
		setSelectedStep,
		selectedSequence,
		setSelectedSequence
	} = useSequencesContext();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	function handleAddStep() {
		if (!selectedTemplate || !selectedSequence) return;
		const oldSteps = selectedTemplate?.steps || [];
		const stepToAdd = {
			id: Math.random().toString(36).substring(2, 9),
			subject: '',
			body: '',
			sendAfterInterval: 3,
			sendAfterType: 'DAYS',
			order: oldSteps.length,
			justCreated: true
		};
		const newSteps = [...oldSteps, stepToAdd];

		setSelectedTemplate({
			...selectedTemplate,
			steps: newSteps
		});
		setSelectedStep(stepToAdd);
		setSelectedSequence({
			...selectedSequence,
			steps: newSteps
		});

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: selectedSequence.id,
			stepNumber: newSteps.length,
			eventName: 'user_added_a_new_sequence_step'
		});

		setTimeout(() => {
			if (scrollRef.current) {
				scrollRef.current.scrollTo(0, scrollRef.current.scrollHeight);
			}
		}, 0);
	}

	const cantAddMoreSteps = (selectedTemplate?.steps || []).length >= 7;

	const stepsSorted = useMemo(() => {
		if (!selectedTemplate) return [];
		return selectedTemplate.steps.sort((a, b) => a.order - b.order);
	}, [selectedTemplate]);

	return (
		<div className="mr-2 mt-2 flex h-full flex-col overflow-hidden rounded-lg border border-dark-grey">
			<div className="flex h-24 flex-shrink-0 flex-col justify-center border-b border-dark-grey bg-dm-black pl-8">
				<NameWithEdit />
				<span>
					{selectedTemplate?.steps.length} Step
					{(selectedTemplate?.steps.length || 0) === 1 ? '' : 's'}
				</span>
			</div>
			<div
				ref={scrollRef}
				className="flex h-full flex-col overflow-y-auto"
			>
				{stepsSorted.map((step, index) => (
					<StepItem
						key={index + step.subject}
						step={step}
						index={index}
					/>
				))}

				<button
					onClick={handleAddStep}
					disabled={cantAddMoreSteps}
					className="flex h-24 w-full flex-shrink-0 items-center gap-4 border-b border-dark-grey bg-dm-black3 pl-8"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						strokeWidth={1.5}
						stroke="currentColor"
						className={`h-8 w-8 $${
							!cantAddMoreSteps &&
							'text-orange group-hover:text-black'
						} `}
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M12 4.5v15m7.5-7.5h-15"
						/>
					</svg>
					<span
						className={`text-xl font-medium ${!cantAddMoreSteps && 'text-gray-200'} `}
					>
						Add step
					</span>
					{cantAddMoreSteps && (
						<span className="text-dm-orange">
							You have reached the max amount of steps
						</span>
					)}
				</button>
			</div>
		</div>
	);
}

function NameWithEdit() {
	const {
		selectedTemplate,
		selectedSequence,
		setSelectedSequence,
		sequences,
		setSequences
	} = useSequencesContext();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const [isEditing, setIsEditing] = useState(false);
	const [name, setName] = useState(selectedSequence?.name || 'New Sequence');

	function handleIsEditing() {
		setIsEditing(!isEditing);
	}

	function handleCancelEdit() {
		setIsEditing(false);
		setName(selectedSequence?.name || 'New Sequence');
	}

	function handleFormSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setIsEditing(false);
		if (!selectedSequence || !selectedTemplate) return;

		if (!selectedSequence.id) {
			// Sequence doest exist yet
			setSelectedSequence({
				...selectedSequence,
				name
			});
			return;
		}

		// Sequence already exists
		updateSequence({
			campaignId: selectedSequence.id,
			name
		})
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error editing sequence name! Please try again.'
					);
				}
				setSequences(
					sequences?.map(c => {
						if (c.id === selectedSequence.id) {
							return {
								...c,
								name
							};
						}
						return c;
					})
				);
				setSelectedSequence({
					...selectedSequence,
					name
				});

				sendAnalytics({
					type: 'track',
					userId: user.email,
					sequenceId: selectedSequence.id,
					eventName: 'user_edited_sequence_name'
				});
			})
			.catch(err => {
				toast.error(err.message);
				setName(selectedSequence?.name || 'New Sequence');
			});
	}

	return (
		<div className="flex items-center gap-6">
			{isEditing ? (
				<>
					<form
						className="flex items-center gap-2"
						onSubmit={handleFormSubmit}
					>
						<input
							type="text"
							className="bg-gray-200 text-gray-800"
							placeholder="Name your template here"
							value={name}
							onChange={e => setName(e.target.value)}
						/>
						<div className="flex flex-col items-center gap-3 text-white">
							<button
								id="campaigns-rename-checkmark"
								type="submit"
							>
								<Checkmark width={20} />
							</button>
							<button onClick={handleCancelEdit} type="button">
								<CloseThin width={14} />
							</button>
						</div>
					</form>
				</>
			) : (
				<>
					<span className="text-xl text-orange">
						{selectedSequence?.name}
					</span>
					<button onClick={handleIsEditing}>
						<EditIcon size={5} />
					</button>
				</>
			)}
		</div>
	);
}

function StepItem({ step, index }: { step: StepsType; index: number }) {
	const { selectedStep, setSelectedStep } = useSequencesContext();

	const { stepDropdownMenuOptions } = useSteps(step);

	const isSelected = step.id === selectedStep?.id;

	function handleSelectStep() {
		setSelectedStep(step);
	}

	return (
		<div
			onClick={handleSelectStep}
			className={`flex h-24 flex-shrink-0 cursor-pointer items-center gap-3 border-b bg-dm-black pl-8 hover:bg-dm-black3 ${isSelected ? 'border-orange bg-dm-black3' : 'border-dark-grey'} `}
		>
			<div className="h-6 w-6 flex-shrink-0">
				<CircleNumber
					number={index + 1}
					variant={isSelected ? 'filled' : 'outlined'}
				/>
			</div>
			<div className="flex w-full flex-col overflow-hidden">
				<div className="flex items-center gap-10 text-lg text-gray-200">
					<span className="whitespace-nowrap">
						{index === 0
							? 'Initial'
							: `After ${step.sendAfterInterval} Day${
									step.sendAfterInterval > 1 ? 's' : ''
								}`}
					</span>
					<span className="whitespace-nowrap font-medium">
						{step.subject}
					</span>
				</div>
				<span>Automatic Email</span>
			</div>
			<div className="mr-3 flex-shrink-0">
				<DropdownMenu
					options={stepDropdownMenuOptions}
					itemClassName="p-2 hover:bg-dm-black3"
				>
					<HorizontalDotsButton />
				</DropdownMenu>
			</div>
		</div>
	);
}
